import _ from 'lodash';
import { useState, useContext, useRef, useEffect, createContext } from "react";
import { DocTile } from "./doc-tile";
import { LeftAlignedButtons } from '../reusable-elements/right-aligned-buttons';
import Comparer from "../../app/comparer";
import { DocUploadModal } from './doc-upload-modal';
import { FileApi } from '../../api/file/file-api';
import { useMsal, } from '@azure/msal-react';
import { allowableDocTypes } from '../../api/docs/doc-functions';
import { arrayAny } from '../../utils/arrayUtils';

export const DocTiles = ({ docs, docTypes, documentHolder, showDelete, containerName, contextType }) => {

    const comparer = new Comparer();

    const [dynamicContext, setDynamicContext] = useState(createContext());


    useEffect(() => {
        const loadContext = async () => {
            if (contextType === 'landlord') {
                const { LandlordContext } = await import('../../pages/company/landlord-detail');
                setDynamicContext(LandlordContext);
            } else if (contextType === 'tenant') {
                const { TenantContext } = await import('../../pages/tenants/tenant-detail');
                setDynamicContext(TenantContext);
            } else if (contextType === 'property') {
                const { PropertyContext } = await import('../../pages/properties/property-detail');
                setDynamicContext(PropertyContext);
            }
        };

        if (contextType) {
            loadContext();
        }
    }, [contextType]);

    const context = useContext(dynamicContext);

    const childRefs = useRef([]); // Initialize an array of refs

    const { instance, inProgress, accounts } = useMsal();

    const [state, setState] = useState(docs);
    const [originalState, setOriginalState] = useState(structuredClone(docs));
    const [isSaving, setIsSaving] = useState(false);

    const [showUpload, setShowUpload] = useState(false);
    const [files, setFiles] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(null);
    const [uploadInProgress, setUploadInProgress] = useState(false);

    const canSave = () => !comparer.equals(state, originalState);

    const resetState = newState => {

        setState(structuredClone(newState.documentHolder.documents));
        setOriginalState(structuredClone(newState.documentHolder.documents));

        const children = _.filter(childRefs.current, dt => !!dt);
        for (const child of children) {
            child.reset();
        }
    };

    const updateDocs = newDoc => {

        setState(current => {
            const newState = [...current];
            if (newDoc && newDoc.id) {
                const existingDocIndex = _.findIndex(newState, d => d && d.id === newDoc.id);
                newState.splice(existingDocIndex, 1); // Remove current version
                newState.splice(existingDocIndex, 0, newDoc); // Insert new version in its place
            }
            else {
                newState.push(newDoc);
            }
            return newState;
        });
    };

    const save = docsToSave => {
        return new Promise((resolve, reject) => { // eslint-disable-line no-unused-vars

            if (!isSaving) {
                setIsSaving(true)
                context.saveDocs(docsToSave)
                    .then(data => { resetState(data); return resolve(data); })
                    .catch(error => { console.error("An error occurred saving Docs", error); return reject(error); })
                    .finally(() => setIsSaving(false));
            }
            else {
                return reject("Save in progress");
            }
        });
    };

    const totalSize = () => _.sumBy(files.files, f => f.size);

    const handleSetShowUpload = () => {
        setShowUpload(!showUpload)
        setFiles([]);
        setUploadInProgress(false);
        setUploadProgress(0);
    };

    const uploadFiles = () => {

        return new Promise((resolve, reject) => { // eslint-disable-line no-unused-vars

            if (uploadInProgress) {
                return reject("Save already in progress");
            }

            const fileApi = new FileApi(instance, inProgress, accounts);

            setUploadInProgress(true);
            setUploadProgress(0);

            const basePath = `${documentHolder.id}/docs`;

            const newFilePaths = _.map(files.files, f => `${basePath}/${f.name}`);
            const existingFilePaths = (state ?? []).map(d => d.path);

            const hasConflicts = arrayAny(_.intersection(newFilePaths, existingFilePaths));
            if (hasConflicts) {
                setUploadInProgress(false);
                setUploadProgress(0);
                return reject("Do not overwrite existing files. This file is being used by an existing doc record");
            }

            fileApi.uploadFiles(containerName, basePath, files.files, data => {
                const totalRemaining = _.sumBy(data, d => d.remainingBytes);
                const progress = 1 - (totalRemaining / totalSize());
                console.log(`Progress: ${progress * 100} %`);
                setUploadProgress(progress);
            })
                .then(uploadedFiles => {
                    const newDoc = files.doc;
                    const file = uploadedFiles[0];

                    newDoc.path = file.path;

                    const docsToSave = [].concat(state, newDoc);

                    save(docsToSave)
                        .then(result => { setState(result.documentHolder.documents); setOriginalState(structuredClone(result.documentHolder.documents)); })
                        .catch(error => { console.error("An error occurred creating new Document", error); return reject(error) })
                        .finally(() => {
                            setUploadInProgress(false);
                            setUploadProgress(0);
                            handleSetShowUpload();
                            return resolve("Upload Complete");
                        });
                })
                .catch(error => { console.log("Error occurred persisting files.", error); return reject(error); });

        });
    };

    const getAllowableDocTypes = () => {
        const result = docTypes && docTypes.documentTypes ?
            { documentTypes: allowableDocTypes(state, docTypes.documentTypes), formats: docTypes.formats } : { documentTypes: [], formats: [] }
        return result;
    };

    return (
        <>
            <DocUploadModal
                handleCancel={handleSetShowUpload}
                handleSave={uploadFiles}
                setFiles={setFiles}
                show={showUpload}
                totalSize={totalSize()}
                uploadProgress={uploadProgress}
                docTypes={getAllowableDocTypes()}
            />

            <div className="row">
                {arrayAny(state) && state.map((doc, i) =>
                    <DocTile
                        key={i}
                        ref={ref => (childRefs.current[i] = ref)}
                        doc={doc}
                        docTypes={docTypes}
                        onStateChange={updateDocs}
                        showDelete={showDelete}
                        isSaving={isSaving}
                    />
                )}
            </div>

            <div className='row'>
                <LeftAlignedButtons>
                    {!!getAllowableDocTypes && arrayAny(getAllowableDocTypes().documentTypes) ? <button className="btn btn-primary" onClick={handleSetShowUpload}>
                        <i className="fa fa-add"></i>&nbsp;&nbsp;Add
                    </button>
                        :
                        <button disabled={true} className="btn btn-red">
                            <i title={'Either all doc types have been added or there are no valid doc types.'} className="fa fa-x"></i>&nbsp;&nbsp;No Doc Types Available
                        </button>
                    }
                    {canSave() &&
                        <button disabled={isSaving} title='Save' className="btn btn-primary ms-10px" onClick={() => save(state)}>
                            <i title='Save' className={isSaving ? 'fa fa-fw fa-arrows-rotate fa-pulse' : 'fa fa-save'}></i>&nbsp;&nbsp;Save
                        </button>}
                </LeftAlignedButtons>
            </div>
        </>
    );
};